import { useEffect, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import Lenis from 'lenis';

// Premium easing curves (match reference site feel)
const EASE_REVEAL = [0.22, 1, 0.36, 1];
const EASE_EXIT = [0.32, 0, 0.67, 0];

export const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE_REVEAL } },
};

export const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
};

// Reveal-on-scroll wrapper for public pages
export const Reveal = ({ children, className = '', delay = 0, y = 28, as = 'div', ...rest }) => {
  const reduce = useReducedMotion();
  const MotionTag = motion[as] || motion.div;
  if (reduce) {
    const Tag = as;
    return <Tag className={className} {...rest}>{children}</Tag>;
  }
  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, ease: EASE_REVEAL, delay }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
};

// Staggered group + item for feature lists
export const Stagger = ({ children, className = '', ...rest }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className} {...rest}>{children}</div>;
  return (
    <motion.div
      className={className}
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-60px' }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

export const StaggerItem = ({ children, className = '', ...rest }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className} {...rest}>{children}</div>;
  return (
    <motion.div className={className} variants={fadeUp} {...rest}>
      {children}
    </motion.div>
  );
};

// Route change page transition (subtle fade-up)
export const PageTransition = ({ children }) => {
  const location = useLocation();
  const reduce = useReducedMotion();
  if (reduce) return <>{children}</>;
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.35, ease: EASE_REVEAL } }}
        exit={{ opacity: 0, y: -6, transition: { duration: 0.2, ease: EASE_EXIT } }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

// Lenis inertia smooth-scroll — enabled ONLY on public pages (no data grids/dialogs there).
// Internal app pages use native scroll (safe for tables & Radix dialogs) with CSS smooth behavior.
const PUBLIC_PATHS = ['/', '/login', '/request-access', '/privacy', '/terms', '/legal'];

export const SmoothScroll = ({ children }) => {
  const reduce = useReducedMotion();
  const location = useLocation();
  const rafRef = useRef();
  const isPublic = PUBLIC_PATHS.includes(location.pathname);
  useEffect(() => {
    if (reduce || !isPublic) return;
    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    const raf = (time) => {
      lenis.raf(time);
      rafRef.current = requestAnimationFrame(raf);
    };
    rafRef.current = requestAnimationFrame(raf);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lenis.destroy();
    };
  }, [reduce, isPublic]);
  return <>{children}</>;
};
